// Вкладка «Заказы» раздела «Кухня»: позиции на кухню по часу открытия заказа — закрытые (OLAP iiko RMS) и, если период
// включает сегодня, открытые сейчас (живые данные iikoCloud: карточка, штриховка на графике, таблица).
// Только у серверов с подключением iikoCloud (пункт меню и маршрут — по признаку useIikoCloudStatus).
// Период — от учётного дня, не длиннее 31 дня (ограничение iikoCloud); группа — общий выбор ресторана.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, ButtonBase, Skeleton, Typography } from '@mui/material';
import FadeIn from '../../components/motion/FadeIn';
import StickyFiltersBar from '../../components/StickyFiltersBar';
import FiltersTopRow from '../../components/FiltersTopRow';
import PeriodSelector from '../../components/PeriodSelector';
import GroupSelector from '../Metrics/components/GroupSelector';
import { useDesktopLayout } from '../../components/layout/AppLayout';
import { useBusinessDay } from '../../hooks/useBusinessDay';
import { fetchKitchenLoad } from '../../services/dataService';
import { extractErrorMessage, isReportAccessDenied } from '../../utils/errors';
import NoReportAccess from '../../components/NoReportAccess';
import { formatPeriodShort, getPeriodRange } from '../../utils/periods';
import { CARD_SURFACE_SX, MUTED_TEXT_COLOR, SECTION_ACCENTS, dimSx } from '../../styles/tokens';
import KitchenSummary from './components/KitchenSummary';
import KitchenChart from './components/KitchenChart';
import LiveCard from './components/LiveCard';
import OpenOrders from './components/OpenOrders';
import { useKitchenLive } from './useKitchenLive';
import { useVisiblePolling } from './useVisiblePolling';
import { cacheKey, readCache, writeCache } from './kitchenCache';
import { buildKitchenModel } from './kitchenModel';
import { STALE_COLOR, clockTime } from './kitchenFormat';
import { selectionTitle } from '../../utils/restaurant';
import { useKitchenRestaurants } from './useKitchenRestaurants';

const ACCENT = SECTION_ACCENTS['/kitchen'];
// Периоды из PeriodSelector, все не длиннее 31 дня; «Год» и прочие с других экранов — не берём
const KITCHEN_PERIODS = ['today', 'yesterday', 'week', 'month', 'lastWeek', 'lastMonth'];
const POLL_MS = 60 * 1000;

// Временный сбой iikoCloud (отчёты не строятся, пауза, сеть) — не ошибка экрана: спокойная карточка,
// данные появятся сами при следующем удачном запросе. Остальное (неверный период и т. п.) — красная плашка
const CLOUD_TEMPORARY_CODES = new Set([
  'iikocloud_backoff',
  'iikocloud_rate_limited',
  'iikocloud_budget_exhausted',
  'iikocloud_unavailable',
  'iikocloud_network',
  'iikocloud_request_failed',
]);

function isCloudTemporary(err) {
  const detail = err?.response?.data?.detail;
  if (detail?.source !== 'iikocloud' || !CLOUD_TEMPORARY_CODES.has(detail.code)) return false;
  // Ответ iikoCloud с 4xx (кроме 429) — не временный сбой
  return detail.code !== 'iikocloud_request_failed' || !detail.status || detail.status >= 500;
}

function initialPeriod() {
  const saved = localStorage.getItem('selectedPeriod');
  return KITCHEN_PERIODS.includes(saved) ? saved : 'today';
}

// Скелетон графика: заголовок, чипы легенды и столбики разной высоты — по форме KitchenChart
const SKELETON_BARS = [18, 26, 40, 62, 85, 70, 48, 55, 78, 92, 66, 38, 24, 14];

function ChartSkeleton() {
  return (
    <Box aria-busy="true" aria-label="Загрузка графика" sx={{ ...CARD_SURFACE_SX, p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Skeleton variant="text" width={180} height={28} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {[72, 64, 88].map((width) => (
          <Skeleton key={width} variant="rounded" width={width} height={24} sx={{ borderRadius: '12px' }} />
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: '4px', md: '8px' }, height: 200 }}>
        {SKELETON_BARS.map((height, index) => (
          <Skeleton key={index} variant="rounded" sx={{ flex: 1, height: `${height}%`, borderRadius: '4px' }} />
        ))}
      </Box>
    </Box>
  );
}

// «В среднем за день» / «Всего за период» — для периодов больше одного дня с продажами
function ModeSwitch({ mode, onChange }) {
  const options = [
    { value: 'average', label: 'В среднем за день' },
    { value: 'total', label: 'Всего за период' },
  ];
  return (
    <Box role="radiogroup" aria-label="Как считать" sx={{ display: 'inline-flex', p: '3px', gap: '3px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.06)' }}>
      {options.map((option) => {
        const active = option.value === mode;
        return (
          <ButtonBase
            key={option.value}
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            sx={{
              minHeight: 38,
              px: 1.5,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              color: active ? '#fff' : MUTED_TEXT_COLOR,
              backgroundColor: active ? `rgba(${ACCENT.rgb},0.22)` : 'transparent',
              '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '2px' },
            }}
          >
            {option.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// tabs — переключатель «Заказы | Стоп-лист» из KitchenPage: в закреплённой панели под строкой ресторана
export default function KitchenLoadTab({ selectedGroup, allGroups, activeServerName, setSelectedGroup, available, tabs = null }) {
  const desktop = useDesktopLayout();
  const [period, setPeriod] = useState(initialPeriod);
  const [mode, setMode] = useState('average');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cloudDown, setCloudDown] = useState(false); // отчёты iikoCloud сейчас не отвечают
  const [accessDenied, setAccessDenied] = useState(false); // нет прав на отчёты iiko — мягкая карточка
  const [selectedHour, setSelectedHour] = useState(null);

  const businessDay = useBusinessDay();
  const dateRange = useMemo(() => (businessDay ? getPeriodRange(period, businessDay) : null), [period, businessDay]);
  const token = localStorage.getItem('token');
  // Фильтр API из выбора в шапке и рестораны сети без iikoCloud (их выбрать нельзя)
  const { filter, unavailable } = useKitchenRestaurants({ available, selectedGroup, allGroups });
  // Открытые заказы есть только сегодня: живая часть и опрос закрытых — для периодов с текущим учётным днём
  const liveEnabled = Boolean(available && businessDay && dateRange && dateRange.to >= businessDay);

  const key = useMemo(() => cacheKey('load', token, activeServerName, dateRange, filter), [token, activeServerName, dateRange, filter]);
  const requestRef = useRef(0); // ответ на устаревший запрос (сменился период/группа) не применяем
  const foregroundRef = useRef(0); // id идущей загрузки по смене периода/группы (0 — нет): фоновый опрос не мешает

  // background — опрос раз в минуту: данные обновляются без индикатора загрузки и без сброса выбранного часа;
  // ошибка фонового запроса не показывается (остаются прежние данные)
  const loadReport = useCallback(async (background) => {
    if (!available || !token || !dateRange) return;
    if (background && foregroundRef.current) return;
    const request = ++requestRef.current;
    if (!background) {
      foregroundRef.current = request;
      setLoading(true);
      setError(null);
    }
    try {
      const report = await fetchKitchenLoad(token, dateRange, filter);
      if (request !== requestRef.current) return;
      setData(report);
      writeCache(key, report);
      setCloudDown(false);
      setAccessDenied(false);
      if (!background) setSelectedHour(null); // по умолчанию график сам выберет текущий час или пиковый
    } catch (err) {
      if (!background && request === requestRef.current) {
        if (isReportAccessDenied(err)) {
          setData(null);
          setAccessDenied(true);
        } else if (isCloudTemporary(err)) {
          // Данные прежнего выбора (другой ресторан или период) не показываем под видом нового
          setData(null);
          setCloudDown(true);
        } else {
          setError(extractErrorMessage(err));
        }
      }
    } finally {
      // Индикатор снимает только последняя загрузка по смене периода/группы
      if (!background && foregroundRef.current === request) {
        foregroundRef.current = 0;
        setLoading(false);
      }
    }
  }, [available, token, dateRange, filter, key]);

  // Смена периода или ресторана, возврат на вкладку: прежний отчёт по этому выбору (если свежий) — сразу,
  // свежий подменяется фоновым запросом; нет — обычная загрузка с индикатором
  useEffect(() => {
    const cached = readCache(key);
    if (!cached) {
      loadReport(false);
      return;
    }
    foregroundRef.current = 0; // идущая загрузка прежнего выбора больше не нужна (её ответ отбросит requestRef)
    setLoading(false);
    setData(cached);
    setSelectedHour(null);
    setError(null);
    setCloudDown(false);
    setAccessDenied(false);
    loadReport(true);
  }, [key, loadReport]);

  // Закрытые — вместе с открытыми раз в минуту, пока экран виден: закрытый заказ появляется в закрытых
  // вскоре после того, как пропал из открытых (бэкенд кеширует закрытые за сегодня на 60 с)
  useVisiblePolling(liveEnabled, POLL_MS, () => loadReport(true));

  const handlePeriodChange = (value) => {
    setPeriod(value);
    localStorage.setItem('selectedPeriod', value);
  };

  // Ресторан сети есть в отчётах iiko RMS, но не среди организаций iikoCloud — на этом экране данных нет
  const notConnected = data?.available === false;
  const liveOn = liveEnabled && !notConnected;

  const severalDays = (data?.days_with_sales || 0) > 1;
  const perDay = severalDays && mode === 'average';
  const block = data && !notConnected ? (perDay ? data.average : data.total) : null;

  const { live, failed: liveFailed, lastUpdated } = useKitchenLive({ enabled: liveOn, filter, server: activeServerName });
  const liveShown = liveOn && !liveFailed && live?.available;
  // Единое правило итогов: закрытые + открытые. Не добавляем открытые в среднем за день (они только
  // за сегодня) и когда живые данные устарели (stale): заказ мог уже закрыться и попасть в закрытые
  const includeOpen = Boolean(liveShown && !perDay && !live.stale);
  const model = useMemo(
    () => (block ? buildKitchenModel({ block, places: data.places, live, includeOpen }) : null),
    [block, data, live, includeOpen]
  );

  // «Сейчас» на графике — по местному времени ресторана из живых данных и только для «Сегодня»
  const isToday = period === 'today';
  const nowLocal = isToday && liveOn && !liveFailed ? live?.local_now || null : null;
  const nowProblem = !isToday || !live ? null : liveFailed ? 'live_failed' : live.local_now ? null : 'no_time_zone';
  useEffect(() => {
    if (nowProblem === 'no_time_zone') {
      console.warn('Загрузка кухни: местное время ресторана не получено (у групп терминалов iikoCloud нет timeZone) — отметка «сейчас» не показывается');
    } else if (nowProblem === 'live_failed') {
      console.warn('Загрузка кухни: живые данные недоступны — местного времени ресторана нет, отметка «сейчас» не показывается');
    }
  }, [nowProblem]);

  const empty = data && !notConnected && !loading && (!model || model.hours.length === 0);
  const waiting = !available || !businessDay || (loading && !data);
  // Смена периода или группы при уже показанных данных — старые остаются приглушёнными
  const dimmed = loading && Boolean(data);

  return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#fff', width: '100%' }}>
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              mb: 2,
              width: '100%',
              backgroundColor: 'rgba(255, 50, 50, 0.1)',
              border: '1px solid rgba(255, 100, 100, 0.3)',
              color: '#fff',
              whiteSpace: 'pre-line',
            }}
          >
            {error}
          </Alert>
        )}

        <StickyFiltersBar>
          <FiltersTopRow>
            {/* Десктоп: выбор ресторана и вкладки — одной группой слева, на одной высоте */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, maxWidth: '100%' }}>
              {allGroups && (
                <GroupSelector
                  groups={allGroups}
                  selectedGroup={selectedGroup || 'Все'}
                  onSelectGroup={setSelectedGroup}
                  activeServerName={activeServerName}
                  disabledGroups={unavailable}
                  disabledHint="нет в iikoCloud"
                  sx={{ mb: 0 }}
                />
              )}
              {desktop && tabs}
            </Box>
          </FiltersTopRow>
          {!desktop && tabs}
          <PeriodSelector period={period} rangeLabel={formatPeriodShort(dateRange)} onChange={handlePeriodChange} showFilters={false} />
        </StickyFiltersBar>

        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
          {severalDays && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <ModeSwitch mode={mode} onChange={setMode} />
              <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>Дней с продажами: {data.days_with_sales}</Typography>
            </Box>
          )}

          {notConnected && (
            <Box sx={{ ...CARD_SURFACE_SX, ...dimSx(dimmed), p: 3, textAlign: 'center' }}>
              <Typography sx={{ color: '#e4e4e7' }}>
                Ресторан «{selectionTitle(selectedGroup, allGroups || [])}» не подключён к iikoCloud — на этом экране данных нет.
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: '13px', color: MUTED_TEXT_COLOR }}>
                Выручка и себестоимость по нему — на других экранах.
              </Typography>
            </Box>
          )}

          {!notConnected && liveOn && <LiveCard live={live} failed={liveFailed} lastUpdated={lastUpdated} group={selectionTitle(selectedGroup, allGroups || [])} />}

          {/* Отчёт сейчас не построился — последний удачный отчёт по закрытым заказам */}
          {data?.stale && (
            <Typography role="status" sx={{ fontSize: '13px', color: STALE_COLOR }}>
              Закрытые заказы — данные на {clockTime(data.updated_at)}, отчёт сейчас не обновляется
            </Typography>
          )}

          {accessDenied && !data && <NoReportAccess />}

          {cloudDown && !accessDenied && !data && (
            <Box sx={{ ...CARD_SURFACE_SX, p: 3, textAlign: 'center' }}>
              <Typography sx={{ color: '#e4e4e7' }}>Отчёты iikoCloud сейчас не отвечают.</Typography>
              <Typography sx={{ mt: 0.5, fontSize: '13px', color: MUTED_TEXT_COLOR }}>
                {liveEnabled
                  ? 'Закрытые заказы появятся сами — экран проверяет раз в минуту.'
                  : 'Обычно это ненадолго — загляните через пару минут.'}
              </Typography>
              <ButtonBase
                onClick={() => loadReport(false)}
                disabled={loading}
                sx={{ mt: 1.5, px: 1.5, minHeight: 36, borderRadius: '8px', fontSize: '14px', fontWeight: 600, color: ACCENT.tab }}
              >
                Проверить сейчас
              </ButtonBase>
            </Box>
          )}

          {!notConnected && !((cloudDown || accessDenied) && !data) && (
            <Box sx={dimSx(dimmed)}>
              <KitchenSummary model={model} perDay={perDay} loading={waiting} />
            </Box>
          )}

          {notConnected || ((cloudDown || accessDenied) && !data) ? null : waiting ? (
            <ChartSkeleton />
          ) : empty ? (
            <Box sx={{ ...CARD_SURFACE_SX, p: 3, textAlign: 'center' }}>
              <Typography sx={{ color: '#e4e4e7' }}>За этот период позиций на кухню нет.</Typography>
            </Box>
          ) : model ? (
            // Приглушение — на обёртке: Fade сам задаёт opacity своему элементу
            <Box sx={dimSx(dimmed)}>
              <FadeIn>
                <Box sx={{ position: 'relative' }}>
                  <KitchenChart
                    model={model}
                    selectedHour={selectedHour}
                    onSelectHour={setSelectedHour}
                    desktop={desktop}
                    nowLocal={nowLocal}
                  />
                </Box>
              </FadeIn>
            </Box>
          ) : null}

          {liveShown && <OpenOrders live={live} places={data?.places} desktop={desktop} />}

          <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR, textAlign: { xs: 'center', md: 'left' } }}>
            Позиции по часу открытия заказа. Бар не учитывается.
          </Typography>
        </Box>
      </Box>
  );
}
